import { useMemo } from 'react'
import { useMe } from '@/features/settings/hooks/useMe'
import type { MeData } from '@/types/api.types'
import type { PermissionBundle } from '@/types/role.types'

/**
 * Merges role bundles with per-user additionalPermissions and restrictedPermissions.
 * Priority: role actions + additional actions - restricted actions (per module).
 * All data comes from GET /users/me; no frontend role logic.
 */
function getBundlesFromMe(me: MeData | undefined): PermissionBundle[] {
  if (!me) return []

  const roleBundles: PermissionBundle[] =
    me.roleId && typeof me.roleId !== 'string' ? (me.roleId.permissions ?? []) : []

  const additional: PermissionBundle[] = me.additionalPermissions ?? []
  const restricted: PermissionBundle[] = me.restrictedPermissions ?? []

  // Build a map: module -> Set<action>
  const byModule = new Map<string, Set<string>>()

  const addToMap = (bundles: PermissionBundle[]): void => {
    for (const bundle of bundles) {
      const mod = bundle.module.toLowerCase()
      if (!byModule.has(mod)) byModule.set(mod, new Set())
      for (const action of bundle.actions) {
        byModule.get(mod)!.add(action.toLowerCase())
      }
    }
  }

  const removeFromMap = (bundles: PermissionBundle[]): void => {
    for (const bundle of bundles) {
      const mod = bundle.module.toLowerCase()
      const existing = byModule.get(mod)
      if (!existing) continue
      for (const action of bundle.actions) {
        existing.delete(action.toLowerCase())
      }
    }
  }

  addToMap(roleBundles)
  addToMap(additional)
  removeFromMap(restricted)

  return Array.from(byModule.entries())
    .filter(([, actions]) => actions.size > 0)
    .map(([module, actions]) => ({ module, actions: Array.from(actions) }))
}

/**
 * Returns true if any bundle grants the given module and action.
 * All permission data comes from the API response; no frontend role logic.
 */
export function hasPermission(
  bundles: PermissionBundle[],
  module: string,
  action: string
): boolean {
  const mod = module.toLowerCase()
  const act = action.toLowerCase()
  return bundles.some(
    (b) => b.module.toLowerCase() === mod && b.actions.some((a) => a.toLowerCase() === act)
  )
}

/**
 * Returns true if the user has at least one action for the given module (from API response).
 * Use when route access should follow "any permission in this module" so backend can add/change actions without frontend changes.
 */
export function hasModule(
  bundles: PermissionBundle[],
  module: string
): boolean {
  const mod = module.toLowerCase()
  return bundles.some((b) => b.module.toLowerCase() === mod && Array.isArray(b.actions) && b.actions.length > 0)
}

export function hasAnyPermission(
  bundles: PermissionBundle[],
  module: string,
  actions: string[]
): boolean {
  return actions.some((action) => hasPermission(bundles, module, action))
}

export interface UsePermissionsResult {
  /** True if API response includes this module+action in roleId.permissions. */
  can: (module: string, action: string) => boolean
  /** True if API response includes any of the given actions for the module. */
  canAny: (module: string, actions: string[]) => boolean
  /** True if API response includes any permission for this module (dynamic: backend can add/remove actions). */
  hasModule: (module: string) => boolean
  /** Raw permission bundles from GET /users/me; do not derive access from anything else. */
  permissions: PermissionBundle[]
  isLoading: boolean
  error: Error | null
}

/**
 * Access control is driven entirely by GET /users/me response (data.roleId.permissions).
 * When the API changes modules or actions, UI updates without frontend changes.
 */
export function usePermissions(): UsePermissionsResult {
  const { data: me, isLoading, error } = useMe()
  const permissions = useMemo(() => getBundlesFromMe(me), [me])
  const can = useMemo(
    () => (module: string, action: string) => hasPermission(permissions, module, action),
    [permissions]
  )
  const canAny = useMemo(
    () => (module: string, actions: string[]) => hasAnyPermission(permissions, module, actions),
    [permissions]
  )
  const hasModuleAccess = useMemo(
    () => (module: string) => hasModule(permissions, module),
    [permissions]
  )
  return {
    can,
    canAny,
    hasModule: hasModuleAccess,
    permissions,
    isLoading,
    error: error ?? null,
  }
}
